import './styles.scss';

import { ActivityType, generateSearchActivityQuery, getActivityConfig } from '@/api/services/activity/SearchActivity';
import { ListGroup, ListGroupProps } from 'react-bootstrap';
import { QueryInputType, getClasses, queryInput } from '@/utils';
import { ReactElement, Ref, forwardRef, useEffect, useState } from 'react';

import { ActivityDisplayMessage } from '@/models/gen/graphql';
import ActivityItem from '@/common/Activity/ActivityItem';
import { LoadingBlur } from '@/components/LoadingSpinner';
import { SortDirectionEnum } from '@/models/gen/graphql';
import graphApi from '@/api';

export type ActivityListProps = ListGroupProps & {
  type: ActivityType;
  resourceId?: string;
  parentResourceId?: string;
};

export type ActivityListPayload = {
  resourceId?: string;
  parentResourceId?: string;
};

export type ActivityListItem = Array<ActivityDisplayMessage>;

const ActivityList = ({ type, resourceId, parentResourceId, ...props }: ActivityListProps, ref: Ref<HTMLDivElement>): ReactElement => {
  const config = getActivityConfig(type);
  const queryDocument = generateSearchActivityQuery(type);
  const [, request] = graphApi<any>(queryDocument);
  const [list, setList] = useState<ActivityListItem>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const getActivity = async (payload: ActivityListPayload) => {
    try {
      setLoading(true);
      const requestPayload = generatePayload(payload);
      const res = await request(requestPayload);
      const items = res?.[config.searchName]?.[config.connectionName]?.edges?.map((edge: any) => edge.node);
      setList(items || []);
    } catch (err) {
      console.error('Error fetching activity list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!resourceId && !parentResourceId) return;
    getActivity({ resourceId, parentResourceId });
  }, [resourceId, parentResourceId]);

  return (
    <ListGroup {...props} className={getClasses('ActivityList')} ref={ref}>
      <LoadingBlur loading={loading} />
      {!list?.length && !loading && (
        <div className="ActivityList-Empty">
          <h5>No activity found.</h5>
        </div>
      )}
      {list.map(
        (item: ActivityDisplayMessage): ReactElement => (
          <ActivityItem item={item} key={item.id} />
        )
      )}
    </ListGroup>
  );
};

const generatePayload = (payload: ActivityListPayload) => ({
  input: {
    first: null,
    after: null,
    query: [
      {
        createdAt: queryInput([], QueryInputType.DEFAULT, SortDirectionEnum.Desc, 0),
        action: queryInput([], QueryInputType.DEFAULT, SortDirectionEnum.Desc, 1),
        resourceId: payload.resourceId ? queryInput(payload.resourceId) : null,
      },
      {
        parentResourceId: payload.parentResourceId ? queryInput(payload.parentResourceId) : null,
      },
    ],
  },
});

export default forwardRef(ActivityList);
